
import React, { useState, useEffect } from "react";
import { ArrowDownIcon, PlusCircle, Inbox, Calendar, Clock, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";

// Usando os dados simulados para demonstração
// Em um caso real, estes dados seriam obtidos de uma API ou do contexto da aplicação
const recebimentosProximos = [
  {
    id: "rec1",
    descricao: "Pagamento Cliente Solar",
    valor: 1500.00,
    dataVencimento: new Date(2025, 3, 20),
    cliente: "Empresa Solar Ltda",
    status: "pendente"
  },
  {
    id: "rec2",
    descricao: "Mensalidade Condomínio Verde",
    valor: 2750.00,
    dataVencimento: new Date(2025, 3, 25),
    cliente: "Condomínio Verde",
    status: "pendente"
  }
];

const recebimentosEmAberto = [
  {
    id: "rec3",
    descricao: "Instalação Painéis Solares",
    valor: 12000.00,
    dataVencimento: new Date(2025, 3, 10),
    cliente: "Residencial Sol Nascente",
    status: "em_aberto"
  }
];

const recebimentosVencidos = [
  {
    id: "rec4",
    descricao: "Manutenção Trimestral",
    valor: 850.00,
    dataVencimento: new Date(2025, 3, 5),
    cliente: "Comércio Luz Solar",
    status: "vencido"
  }
];

const RecebimentosSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState("proximos");
  const [recebimentos, setRecebimentos] = useState<any[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    // Simulando carregamento de dados
    if (activeTab === "proximos") {
      setRecebimentos(recebimentosProximos);
    } else if (activeTab === "abertos") {
      setRecebimentos(recebimentosEmAberto);
    } else if (activeTab === "vencidos") {
      setRecebimentos(recebimentosVencidos);
    }
  }, [activeTab]);

  const handleNovoRecebimento = () => {
    // Navegar para o formulário de novo recebimento
    const event = new CustomEvent('change-tab', { 
      detail: { tab: 'recebimentos', action: 'novo-recebimento' } 
    });
    window.dispatchEvent(event);
    
    toast({
      title: "Novo recebimento",
      description: "Redirecionando para o formulário de agendamento de recebimentos",
    });
  };

  const getTabIcon = (tab: string) => {
    switch (tab) {
      case "proximos": return <Calendar className="h-4 w-4 mr-2" />;
      case "abertos": return <Clock className="h-4 w-4 mr-2" />;
      case "vencidos": return <AlertTriangle className="h-4 w-4 mr-2" />;
      default: return null;
    }
  };

  // Calcular o total dos recebimentos
  const totalRecebimentos = recebimentos.reduce((acc, item) => acc + item.valor, 0);

  return (
    <div className="h-full">
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div className="flex items-center">
          <ArrowDownIcon className="h-4 w-4 text-gray-500" />
          <h2 className="text-base ml-2 text-gray-700">Recebimentos</h2>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-600">
            {totalRecebimentos.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          </p>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full bg-gray-50 rounded-none border-b border-gray-100">
          <TabsTrigger 
            value="proximos" 
            className={`flex-1 py-2 px-4 text-center text-xs ${activeTab === "proximos" ? "border-b-2 border-gray-400 font-normal" : "text-gray-500"}`}
          >
            Próximos a receber
          </TabsTrigger>
          <TabsTrigger 
            value="abertos" 
            className={`flex-1 py-2 px-4 text-center text-xs ${activeTab === "abertos" ? "border-b-2 border-gray-400 font-normal" : "text-gray-500"}`}
          >
            Em aberto
          </TabsTrigger>
          <TabsTrigger 
            value="vencidos" 
            className={`flex-1 py-2 px-4 text-center text-xs ${activeTab === "vencidos" ? "border-b-2 border-gray-400 font-normal" : "text-gray-500"}`}
          >
            Vencido
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value={activeTab}>
          {recebimentos.length > 0 ? (
            <div className="">
              <Table>
                <TableHeader className="bg-gray-50">
                  <TableRow>
                    <TableHead className="w-[100px] text-xs text-gray-500">Data</TableHead>
                    <TableHead className="text-xs text-gray-500">Descrição</TableHead>
                    <TableHead className="text-right text-xs text-gray-500">Valor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recebimentos.map(recebimento => (
                    <TableRow key={recebimento.id} className="hover:bg-gray-50">
                      <TableCell className="text-xs text-gray-600">
                        {format(recebimento.dataVencimento, "dd/MM/yyyy")}
                      </TableCell>
                      <TableCell>
                        <p className="text-xs text-gray-600">{recebimento.descricao}</p>
                        <p className="text-xs text-gray-500">{recebimento.cliente}</p>
                      </TableCell>
                      <TableCell className="text-right text-xs text-gray-600">
                        {recebimento.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              <div className="p-3 text-center">
                <Button 
                  variant="link" 
                  className="text-gray-500 hover:text-gray-600 text-xs"
                  onClick={() => {
                    // Navegar para a aba completa de recebimentos
                    const event = new CustomEvent('change-tab', { detail: { tab: 'recebimentos' } });
                    window.dispatchEvent(event);
                  }}
                >
                  Ver todos os recebimentos
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-40 text-gray-500">
              <div className="text-center">
                <Inbox className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p className="text-xs">Nenhum recebimento {activeTab === "proximos" ? "pendente" : activeTab === "abertos" ? "em aberto" : "vencido"}</p>
                <Button 
                  className="mt-4 text-xs py-1 h-auto bg-gray-200 text-gray-600 hover:bg-gray-300"
                  onClick={handleNovoRecebimento}
                >
                  <PlusCircle className="h-3 w-3 mr-1" /> Novo recebimento
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RecebimentosSection;
